/**
 * Ashluxe Blog Layout JavaScript
 * File: assets/ashluxe-blog-layout.js
 * 
 * Handles interactions for the blog layout including filtering,
 * newsletter signup, and scroll animations
 */

class AshluxeBlogLayout {
  constructor(options = {}) {
    this.options = {
      sectionId: '',
      enableFiltering: true,
      animationEnabled: true,
      ...options
    };
    
    this.section = document.querySelector(`[data-section-id="${this.options.sectionId}"]`);
    this.postsContainer = this.section?.querySelector('.blog-posts-grid');
    this.newsletterForm = this.section?.querySelector('.newsletter-form');
    this.topicLinks = this.section?.querySelectorAll('.topic-link');
    this.tagCloudItems = this.section?.querySelectorAll('.tag-cloud-item');
    
    this.currentFilter = 'all';
    this.isLoading = false;
    
    this.init();
  }
  
  init() {
    if (!this.section) {
      console.warn('Ashluxe Blog Layout section not found');
      return;
    }
    
    this.setupFiltering();
    this.setupNewsletter();
    this.setupAnimations();
    this.setupScrollToTop();
    this.setupImageLazyLoading();
    
    console.log('Ashluxe Blog Layout initialized');
  }
  
  /**
   * Setup tag filtering functionality
   */
  setupFiltering() {
    if (!this.options.enableFiltering) return;
    
    // Add click handlers to topic links
    this.topicLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const tag = this.extractTagFromUrl(link.href);
        this.filterByTag(tag);
      });
    });
    
    // Add click handlers to tag cloud items
    this.tagCloudItems.forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const tag = this.extractTagFromUrl(item.href);
        this.filterByTag(tag);
      });
    });
  }
  
  /**
   * Extract tag from URL
   */
  extractTagFromUrl(url) {
    try {
      const matches = url.match(/\/tagged\/([^\/\?]+)/);
      return matches ? decodeURIComponent(matches[1]) : '';
    } catch (error) {
      console.error('Error extracting tag from URL:', error);
      return '';
    }
  }
  
  /**
   * Filter posts by tag
   */
  filterByTag(tag) {
    if (!tag || this.currentFilter === tag || !this.postsContainer) return;
    
    this.currentFilter = tag;
    const posts = this.postsContainer.querySelectorAll('.blog-post-card');
    
    posts.forEach((post, index) => {
      const postTags = post.dataset.tags?.toLowerCase() || '';
      const shouldShow = postTags.includes(tag.toLowerCase());
      
      if (shouldShow) {
        post.style.display = 'block';
        if (this.options.animationEnabled) {
          setTimeout(() => {
            post.classList.add('visible');
          }, index * 100);
        }
      } else {
        post.style.display = 'none';
        post.classList.remove('visible');
      }
    });
    
    // Update URL without page reload
    try {
      const newUrl = `${window.location.pathname}?tag=${encodeURIComponent(tag)}`;
      window.history.pushState({ tag }, '', newUrl);
    } catch (error) {
      console.error('Error updating URL:', error);
    }
    
    // Scroll to top of posts
    if (this.postsContainer) {
      this.postsContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }
  
  /**
   * Setup newsletter form submission
   */
  setupNewsletter() {
    if (!this.newsletterForm) return;
    
    this.newsletterForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.submitNewsletter();
    });
  }
  
  /**
   * Submit newsletter form
   */
  async submitNewsletter() {
    const formData = new FormData(this.newsletterForm);
    const submitButton = this.newsletterForm.querySelector('.newsletter-submit-btn');
    const successMessage = this.section.querySelector('.newsletter-success');
    const emailInput = this.newsletterForm.querySelector('.newsletter-email-input');
    
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Subscribing...';
    submitButton.disabled = true;
    
    try {
      const response = await fetch(this.newsletterForm.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        // Show success message
        successMessage.style.display = 'block';
        emailInput.value = '';
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          successMessage.style.display = 'none';
        }, 5000);
        
        // Track newsletter signup (if analytics available)
        if (typeof gtag !== 'undefined') {
          gtag('event', 'newsletter_signup', {
            'event_category': 'engagement',
            'event_label': 'blog_sidebar'
          });
        }
      } else {
        throw new Error('Subscription failed');
      }
      
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      this.showNotification('Subscription failed. Please try again.', 'error');
    } finally {
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    }
  }
  
  /**
   * Setup scroll animations
   */
  setupAnimations() {
    if (!this.options.animationEnabled) return;
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);
    
    // Observe all blog post cards
    this.observeElements('.blog-post-card');
  }
  
  /**
   * Observe elements for animations
   */
  observeElements(selector) {
    if (!this.intersectionObserver) return;
    
    const elements = this.section.querySelectorAll(selector);
    elements.forEach(element => {
      element.classList.add('fade-in');
      this.intersectionObserver.observe(element);
    });
  }
  
  /**
   * Setup scroll to top functionality
   */
  setupScrollToTop() {
    // Create scroll to top button
    const scrollButton = document.createElement('button');
    scrollButton.className = 'scroll-to-top';
    scrollButton.innerHTML = '↑';
    scrollButton.style.cssText = `
      position: fixed;
      bottom: 30px;
      right: 30px;
      width: 50px;
      height: 50px;
      background: var(--accent-color, #000);
      color: white;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      font-size: 20px;
      z-index: 1000;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;
    
    document.body.appendChild(scrollButton);
    
    // Show/hide scroll button based on scroll position
    window.addEventListener('scroll', () => {
      if (window.pageYOffset > 300) {
        scrollButton.style.opacity = '1';
        scrollButton.style.visibility = 'visible';
      } else {
        scrollButton.style.opacity = '0';
        scrollButton.style.visibility = 'hidden';
      }
    });
    
    // Scroll to top when clicked
    scrollButton.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
  
  /**
   * Setup image loading and error handling
   */
  setupImageLoading() {
    // Handle main blog post images
    const blogImages = this.section.querySelectorAll('.post-featured-image');
    blogImages.forEach(img => {
      img.addEventListener('load', function() {
        this.style.opacity = '1';
      });
      
      img.addEventListener('error', function() {
        console.log('Image failed to load:', this.src);
        const placeholder = this.closest('.post-image-wrapper').querySelector('.post-image-placeholder');
        if (placeholder) {
          this.style.display = 'none';
          placeholder.style.display = 'flex';
        } else {
          // Create placeholder if it doesn't exist
          const newPlaceholder = document.createElement('div');
          newPlaceholder.className = 'post-image-placeholder';
          newPlaceholder.innerHTML = '<span>Image Unavailable</span>';
          newPlaceholder.style.cssText = `
            width: 100%;
            height: 100%;
            background: #D9D9D9;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #666;
            font-size: 14px;
            text-align: center;
            padding: 20px;
            border-radius: 5px;
            font-weight: 500;
          `;
          this.parentNode.appendChild(newPlaceholder);
          this.style.display = 'none';
        }
      });
    });
    
    // Handle sidebar images
    const sidebarImages = this.section.querySelectorAll('.popular-post-image img, .related-article-image img');
    sidebarImages.forEach(img => {
      img.addEventListener('load', function() {
        this.style.opacity = '1';
      });
      
      img.addEventListener('error', function() {
        console.log('Sidebar image failed to load:', this.src);
        const container = this.closest('.popular-post-image') || this.closest('.related-article-image');
        if (container) {
          const placeholder = document.createElement('div');
          placeholder.style.cssText = `
            width: 80px;
            height: 80px;
            background: #D9D9D9;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            color: #666;
            font-weight: 500;
          `;
          placeholder.textContent = 'No Image';
          container.appendChild(placeholder);
          this.style.display = 'none';
        }
      });
    });
  }
  
  /**
   * Show notification message
   */
  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#ff4444' : '#4CAF50'};
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 1000;
      font-size: 14px;
      max-width: 300px;
      opacity: 0;
      transform: translateX(100%);
      transition: all 0.3s ease;
    `;
    
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Animate in
    setTimeout(() => {
      notification.style.opacity = '1';
      notification.style.transform = 'translateX(0)';
    }, 100);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 5000);
  }
  
  /**
   * Handle browser back/forward navigation
   */
  setupPopState() {
    window.addEventListener('popstate', (event) => {
      if (event.state && event.state.tag) {
        this.filterByTag(event.state.tag);
      } else {
        this.showAllPosts();
      }
    });
  }
  
  /**
   * Show all posts (clear filter)
   */
  showAllPosts() {
    this.currentFilter = 'all';
    const posts = this.postsContainer.querySelectorAll('.blog-post-card');
    
    posts.forEach((post, index) => {
      post.style.display = 'block';
      if (this.options.animationEnabled) {
        setTimeout(() => {
          post.classList.add('visible');
        }, index * 50);
      }
    });
  }
  
  /**
   * Add reading time to posts
   */
  addReadingTime() {
    const posts = this.postsContainer.querySelectorAll('.blog-post-card');
    
    posts.forEach(post => {
      const excerpt = post.querySelector('.post-excerpt');
      if (excerpt) {
        const wordCount = excerpt.textContent.split(' ').length;
        const readingTime = Math.ceil(wordCount / 200); // 200 words per minute
        
        const readingTimeElement = document.createElement('span');
        readingTimeElement.className = 'reading-time';
        readingTimeElement.textContent = `${readingTime} min read`;
        readingTimeElement.style.cssText = `
          font-size: 11px;
          color: var(--medium-gray);
          font-weight: 500;
        `;
        
        const metaContainer = post.querySelector('.post-meta');
        if (metaContainer) {
          metaContainer.appendChild(readingTimeElement);
        }
      }
    });
  }
  
  /**
   * Setup search functionality
   */
  setupSearch() {
    const searchInput = this.section.querySelector('.blog-search-input');
    if (!searchInput) return;
    
    let searchTimeout;
    
    searchInput.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.searchPosts(e.target.value);
      }, 300);
    });
  }
  
  /**
   * Search posts by title and excerpt
   */
  searchPosts(query) {
    const posts = this.postsContainer.querySelectorAll('.blog-post-card');
    const searchTerm = query.toLowerCase().trim();
    
    if (!searchTerm) {
      this.showAllPosts();
      return;
    }
    
    posts.forEach(post => {
      const title = post.querySelector('.post-title')?.textContent.toLowerCase() || '';
      const excerpt = post.querySelector('.post-excerpt')?.textContent.toLowerCase() || '';
      
      const matches = title.includes(searchTerm) || excerpt.includes(searchTerm);
      
      post.style.display = matches ? 'block' : 'none';
    });
  }
  
  /**
   * Destroy the instance and clean up
   */
  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    // Remove scroll to top button
    const scrollButton = document.querySelector('.scroll-to-top');
    if (scrollButton) {
      scrollButton.remove();
    }
    
    // Clean up event listeners
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('popstate', this.handlePopState);
  }
}

// Auto-initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
  const blogSections = document.querySelectorAll('.ashluxe-blog-layout');
  
  blogSections.forEach(section => {
    const sectionId = section.dataset.sectionId;
    if (sectionId) {
      if (!window.ashluxeBlogInstances) {
        window.ashluxeBlogInstances = {};
      }
      
      if (!window.ashluxeBlogInstances[sectionId]) {
        window.ashluxeBlogInstances[sectionId] = new AshluxeBlogLayout({
          sectionId: sectionId
        });
      }
    }
  });
});

// Utility functions
window.AshluxeBlogUtils = {
  /**
   * Filter posts by tag programmatically
   */
  filterByTag(sectionId, tag) {
    const instance = window.ashluxeBlogInstances?.[sectionId];
    if (instance) {
      instance.filterByTag(tag);
    }
  },
  
  /**
   * Show all posts
   */
  showAllPosts(sectionId) {
    const instance = window.ashluxeBlogInstances?.[sectionId];
    if (instance) {
      instance.showAllPosts();
    }
  },
  
  /**
   * Get section instance
   */
  getInstance(sectionId) {
    return window.ashluxeBlogInstances?.[sectionId];
  }
};

// Make AshluxeBlogLayout globally available
window.AshluxeBlogLayout = AshluxeBlogLayout;